import { useTranslation } from 'react-i18next'
import useDialog from '../hooks/useDialog'
import { useMemo } from 'react'
import { Mark } from '@mui/material/Slider/useSlider.types'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormGroup,
  Grid,
  Slider,
  Stack,
  Typography
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { formatTemperature } from '../utils/string'

export interface AuroraTargetTemperatureInputProps {
  value: number
  loading?: boolean
  onChange: (value: number) => void
  onSubmit: (value: number) => void
}

export default function AuroraTargetTemperatureInput({
  value,
  loading = false,
  onChange,
  onSubmit
}: AuroraTargetTemperatureInputProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The dialog hook.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The marks.
   */
  const marks = useMemo(() => {
    const marks: Mark[] = []

    for (let temperature = 0; temperature <= 100; temperature += 20) {
      marks.push({
        value: temperature,
        label: `${temperature}°C`
      })
    }

    return marks
  }, [])

  function handleClick() {
    if (!loading) {
      openDialog()
    }
  }

  function handleChange(_: Event, value: number | number[]) {
    onChange(Array.isArray(value) ? value[0] : value)
  }

  async function handleSubmit() {
    onSubmit(value)
  }

  return (
    <>
      <Typography
        variant="h2"
        color={loading ? 'text.disabled' : 'primary'}
        sx={{ cursor: 'pointer' }}
        onClick={handleClick}
      >
        {formatTemperature(value)}
      </Typography>

      <Dialog open={open} maxWidth="sm" fullWidth onClose={closeDialog}>
        <DialogTitle>{t('setNewTemperature')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormGroup>
                <Box sx={{ px: 4, mt: 8 }}>
                  <Slider
                    value={value}
                    disabled={loading}
                    step={0.5}
                    min={0}
                    max={100}
                    valueLabelDisplay="on"
                    valueLabelFormat={(value) => `${value}°C`}
                    marks={marks}
                    onChange={handleChange}
                  />
                </Box>
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                <Button variant="text" color="inherit" disabled={loading} onClick={closeDialog}>
                  {t('actions:cancel')}
                </Button>

                <LoadingButton
                  variant="contained"
                  color="primary"
                  loading={loading}
                  onClick={handleSubmit}
                >
                  {t('actions:saveChanges')}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  )
}
